/* eslint-disable curly */
import { t, getLanguage } from "@excalidraw/excalidraw/i18n";
import { useCallback, useEffect, useState } from "react";

import { libraryApi } from "../data/libraryApi";
import * as libraryStore from "../data/libraryStore";

import { ConfirmDialog, PromptDialog } from "./LibraryDialogs";

import "./CanvasVersionHistory.scss";

import type {
  CanvasMeta,
  CanvasScene,
  CanvasVersionMeta,
} from "../data/libraryApi";

interface Props {
  canvasId: string;
  onClose: () => void;
  onLibraryChanged: () => void;
  /** Apply a version's scene to the canvas in read-only preview (App snapshots
   *  the live state + arms the autosave guard first). */
  onEnterPreview: (scene: CanvasScene) => void;
  /** Restore the live snapshot and disarm the guard. */
  onExitPreview: () => void;
  /** Server confirmed the restore: App purges pending, applies the scene and
   *  records updated_at (see §5.4 of the implementation doc). */
  onRestored: (scene: CanvasScene, updatedAt: number) => void;
  /** Flush any debounced autosave so a named version captures what's on screen. */
  onFlushAutosave: () => Promise<void>;
  setToast: (message: string) => void;
}

type LoadState = "loading" | "ready" | "offline" | "error";

type DialogState =
  | { type: "name-version" }
  | { type: "restore"; version: CanvasVersionMeta }
  | {
      // Duplicate-as-new-canvas asks for the copy's name first; `source` is the
      // canvas being duplicated (folder/type/page-style are copied from it).
      type: "duplicate";
      version: CanvasVersionMeta;
      suggested: string;
      source: CanvasMeta | null;
    }
  | { type: "delete"; version: CanvasVersionMeta };

const historyIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l3 3" />
  </svg>
);

const starIcon = (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="currentColor"
    stroke="none"
  >
    <path d="M12 2l2.9 6.26L21.5 9.3l-4.75 4.4 1.15 6.55L12 17l-5.9 3.25 1.15-6.55L2.5 9.3l6.6-1.04L12 2z" />
  </svg>
);

// Group versions by recency. `created_at` is epoch ms.
const groupKeyOf = (createdAt: number, now: Date): string => {
  const d = new Date(createdAt);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, now)) return "today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return "yesterday";
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7);
  if (d > weekAgo) return "thisWeek";
  return `month:${d.getFullYear()}-${d.getMonth()}`;
};

export const CanvasVersionHistory = ({
  canvasId,
  onClose,
  onLibraryChanged,
  onEnterPreview,
  onExitPreview,
  onRestored,
  onFlushAutosave,
  setToast,
}: Props) => {
  const [state, setState] = useState<LoadState>("loading");
  const [versions, setVersions] = useState<CanvasVersionMeta[]>([]);
  const [previewing, setPreviewing] = useState<{
    meta: CanvasVersionMeta;
    scene: CanvasScene;
  } | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [busy, setBusy] = useState(false);

  const locale = getLanguage().code;

  const load = useCallback(async () => {
    if (!navigator.onLine) {
      setState("offline");
      return;
    }
    setState("loading");
    try {
      const rows = await libraryApi.listVersions(canvasId);
      setVersions(rows);
      setState("ready");
    } catch {
      setState(navigator.onLine ? "error" : "offline");
    }
  }, [canvasId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Board switch while the panel is open: App already dropped the preview
  // guard (the new board loads live content) — drop the stale local state too.
  useEffect(() => {
    setPreviewing(null);
    setDialog(null);
  }, [canvasId]);

  // Recover automatically when the connection comes back.
  useEffect(() => {
    const onOnline = () => {
      if (state === "offline") void load();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [state, load]);

  const exitPreview = useCallback(() => {
    if (!previewing) return;
    onExitPreview();
    setPreviewing(null);
  }, [previewing, onExitPreview]);

  const closePanel = useCallback(() => {
    exitPreview();
    onClose();
  }, [exitPreview, onClose]);

  // Esc: first leaves preview, then closes the panel. Skipped while a dialog
  // is open (the Dialog component handles its own Esc).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || dialog) return;
      e.stopPropagation();
      if (previewing) exitPreview();
      else onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [dialog, previewing, exitPreview, onClose]);

  const startPreview = async (v: CanvasVersionMeta) => {
    if (busy) return;
    if (previewing?.meta.id === v.id) return;
    setBusy(true);
    try {
      const scene = await libraryApi.getVersionScene(canvasId, v.id);
      onEnterPreview(scene);
      setPreviewing({ meta: v, scene });
    } catch {
      setToast(t("versionHistory.error"));
    } finally {
      setBusy(false);
    }
  };

  const doRestore = async (v: CanvasVersionMeta) => {
    setBusy(true);
    try {
      const scene =
        previewing?.meta.id === v.id
          ? previewing.scene
          : await libraryApi.getVersionScene(canvasId, v.id);
      const { updated_at } = await libraryApi.restoreVersion(canvasId, v.id);
      setPreviewing(null);
      onRestored(scene, updated_at);
      setToast(t("versionHistory.restored"));
      await load(); // the "before restoring" safety version shows up
    } catch {
      setToast(t("versionHistory.error"));
    } finally {
      setBusy(false);
    }
  };

  const doCreateNamed = async (label: string) => {
    setBusy(true);
    try {
      await onFlushAutosave(); // capture what the user sees, not a stale save
      await libraryApi.createNamedVersion(canvasId, label);
      await load();
    } catch {
      setToast(t("versionHistory.error"));
    } finally {
      setBusy(false);
    }
  };

  // Ask for the copy's name first (suggested: "<canvas> (<version date>)").
  const openDuplicateDialog = async (v: CanvasVersionMeta) => {
    if (busy) return;
    let source: CanvasMeta | null = null;
    try {
      source = await libraryApi.getCanvas(canvasId);
    } catch {
      /* keep fallbacks */
    }
    const dateLabel = new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(v.created_at);
    const baseName = source?.name ?? t("canvasLibrary.untitled");
    setDialog({
      type: "duplicate",
      version: v,
      suggested: `${baseName} (${dateLabel})`,
      source,
    });
  };

  const doDuplicate = async (
    v: CanvasVersionMeta,
    name: string,
    source: CanvasMeta | null,
  ) => {
    setBusy(true);
    try {
      const scene =
        previewing?.meta.id === v.id
          ? previewing.scene
          : await libraryApi.getVersionScene(canvasId, v.id);
      // Via libraryStore so the sidebar cache stays coherent; the direct
      // saveScene is fine because the panel is online-only by design.
      const copy = await libraryStore.createCanvas({
        name,
        folder_id: source?.folder_id ?? null,
        canvas_type: source?.canvas_type,
        page_style: source?.page_style ?? undefined,
      });
      await libraryApi.saveScene(copy.id, scene);
      onLibraryChanged();
      setToast(t("versionHistory.duplicated"));
    } catch {
      setToast(t("versionHistory.error"));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (v: CanvasVersionMeta) => {
    setBusy(true);
    try {
      await libraryApi.deleteVersion(canvasId, v.id);
      if (previewing?.meta.id === v.id) exitPreview();
      await load();
    } catch {
      setToast(t("versionHistory.error"));
    } finally {
      setBusy(false);
    }
  };

  // --- formatting helpers ---
  const timeFmt = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dayFmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
  });
  const monthFmt = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  });
  const fullFmt = new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeStyle: "short",
  });

  const now = new Date();
  const groupTitle = (key: string): string => {
    if (key === "today") return t("versionHistory.today");
    if (key === "yesterday") return t("versionHistory.yesterday");
    if (key === "thisWeek") return t("versionHistory.thisWeek");
    const m = key.match(/^month:(\d+)-(\d+)$/);
    if (m) {
      return monthFmt.format(new Date(Number(m[1]), Number(m[2]), 1));
    }
    return t("versionHistory.groupOlder");
  };

  const rowTime = (v: CanvasVersionMeta, key: string) =>
    key === "today" || key === "yesterday"
      ? timeFmt.format(v.created_at)
      : `${dayFmt.format(v.created_at)} ${timeFmt.format(v.created_at)}`;

  const rowLabel = (v: CanvasVersionMeta) => {
    if (v.kind === "named") return v.label ?? t("versionHistory.auto");
    if (v.kind === "restore") return t("versionHistory.restorePoint");
    return t("versionHistory.auto");
  };

  // Ordered groups (versions arrive newest-first from the server).
  const groups: { key: string; items: CanvasVersionMeta[] }[] = [];
  for (const v of versions) {
    const key = groupKeyOf(v.created_at, now);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(v);
    else groups.push({ key, items: [v] });
  }

  const renderRow = (v: CanvasVersionMeta, key: string) => {
    const isPreviewed = previewing?.meta.id === v.id;
    return (
      <div
        key={v.id}
        className={`pw-version-history__row${
          isPreviewed ? " is-previewing" : ""
        }${v.kind === "named" ? " is-named" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => void startPreview(v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            void startPreview(v);
          }
        }}
        title={fullFmt.format(v.created_at)}
      >
        <span className="pw-version-history__row-icon" aria-hidden="true">
          {v.kind === "named" ? starIcon : "○"}
        </span>
        <span className="pw-version-history__row-main">
          <span className="pw-version-history__row-label">{rowLabel(v)}</span>
          <span className="pw-version-history__row-meta">
            {rowTime(v, key)}
            {v.author_name ? ` · ${v.author_name}` : ""}
          </span>
        </span>
        <span className="pw-version-history__row-actions">
          <button
            type="button"
            className="pw-version-history__icon-btn"
            title={t("versionHistory.restore")}
            aria-label={t("versionHistory.restore")}
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              setDialog({ type: "restore", version: v });
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
          <button
            type="button"
            className="pw-version-history__icon-btn"
            title={t("versionHistory.duplicate")}
            aria-label={t("versionHistory.duplicate")}
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              void openDuplicateDialog(v);
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="12" height="12" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
          <button
            type="button"
            className="pw-version-history__icon-btn pw-version-history__icon-btn--danger"
            title={t("versionHistory.delete")}
            aria-label={t("versionHistory.delete")}
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              setDialog({ type: "delete", version: v });
            }}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
            </svg>
          </button>
        </span>
      </div>
    );
  };

  return (
    <>
      {previewing && (
        <div className="pw-version-banner" role="status">
          <span className="pw-version-banner__icon" aria-hidden="true">
            {historyIcon}
          </span>
          <span className="pw-version-banner__text">
            {t("versionHistory.viewingBanner", {
              date: fullFmt.format(previewing.meta.created_at),
            })}
          </span>
          <button
            type="button"
            className="pw-version-banner__btn"
            onClick={exitPreview}
          >
            {t("versionHistory.backToCurrent")}
          </button>
          <button
            type="button"
            className="pw-version-banner__btn pw-version-banner__btn--primary"
            disabled={busy}
            onClick={() =>
              setDialog({ type: "restore", version: previewing.meta })
            }
          >
            {t("versionHistory.restore")}
          </button>
        </div>
      )}

      <div
        className="pw-version-history"
        role="complementary"
        aria-label={t("versionHistory.title")}
      >
        <div className="pw-version-history__header">
          <span className="pw-version-history__title">
            {historyIcon}
            {t("versionHistory.title")}
          </span>
          <button
            type="button"
            className="pw-version-history__icon-btn"
            onClick={closePanel}
            title={t("canvasLibrary.cancel")}
            aria-label={t("canvasLibrary.cancel")}
          >
            ✕
          </button>
        </div>

        {state === "ready" && (
          <button
            type="button"
            className="pw-version-history__save-named"
            disabled={busy}
            onClick={() => setDialog({ type: "name-version" })}
          >
            ⊕ {t("versionHistory.saveNamed")}
          </button>
        )}

        <div className="pw-version-history__body">
          {state === "loading" && (
            <div className="pw-version-history__skeleton" aria-hidden="true">
              {[82, 64, 74, 58, 70].map((w, i) => (
                <div
                  key={i}
                  className="pw-version-history__skeleton-row"
                  style={{ width: `${w}%` }}
                />
              ))}
            </div>
          )}

          {state === "offline" && (
            <div className="pw-version-history__empty">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 16.2A4.5 4.5 0 0 1 6.6 8a6 6 0 0 1 11 2.3 4 4 0 0 1 1.9 7.2" />
                <path d="M3 3l18 18" />
              </svg>
              <p>{t("versionHistory.offline")}</p>
            </div>
          )}

          {state === "error" && (
            <div className="pw-version-history__empty">
              <p>{t("versionHistory.error")}</p>
              <button
                type="button"
                className="pw-version-history__retry"
                onClick={() => void load()}
              >
                {t("versionHistory.retry")}
              </button>
            </div>
          )}

          {state === "ready" && (
            <>
              <div
                className={`pw-version-history__row pw-version-history__row--current${
                  previewing ? "" : " is-previewing"
                }`}
                role="button"
                tabIndex={0}
                onClick={exitPreview}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    exitPreview();
                  }
                }}
              >
                <span
                  className="pw-version-history__row-icon"
                  aria-hidden="true"
                >
                  ●
                </span>
                <span className="pw-version-history__row-main">
                  <span className="pw-version-history__row-label">
                    {t("versionHistory.current")}
                  </span>
                </span>
              </div>

              {versions.length === 0 && (
                <div className="pw-version-history__empty">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    width="28"
                    height="28"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 3" />
                  </svg>
                  <p>{t("versionHistory.empty")}</p>
                  <p className="pw-version-history__hint">
                    {t("versionHistory.emptyHint")}
                  </p>
                </div>
              )}

              {groups.map((g) => (
                <div key={g.key} className="pw-version-history__group">
                  <div className="pw-version-history__group-title">
                    {groupTitle(g.key)}
                  </div>
                  {g.items.map((v) => renderRow(v, g.key))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {dialog?.type === "name-version" && (
        <PromptDialog
          title={t("versionHistory.saveNamedTitle")}
          label={t("versionHistory.namePlaceholder")}
          initialValue=""
          submitLabel={t("canvasLibrary.save")}
          onSubmit={(value) => {
            setDialog(null);
            void doCreateNamed(value);
          }}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.type === "duplicate" && (
        <PromptDialog
          title={t("versionHistory.duplicate")}
          label={t("canvasLibrary.name")}
          initialValue={dialog.suggested}
          submitLabel={t("canvasLibrary.create")}
          onSubmit={(value) => {
            const { version, source } = dialog;
            setDialog(null);
            void doDuplicate(version, value, source);
          }}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.type === "restore" && (
        <ConfirmDialog
          title={t("versionHistory.restoreConfirmTitle")}
          message={t("versionHistory.restoreConfirmMessage", {
            date: fullFmt.format(dialog.version.created_at),
          })}
          confirmLabel={t("versionHistory.restoreConfirmTitle")}
          onConfirm={() => void doRestore(dialog.version)}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.type === "delete" && (
        <ConfirmDialog
          title={t("versionHistory.delete")}
          message={t("versionHistory.deleteConfirm")}
          confirmLabel={t("canvasLibrary.delete")}
          danger
          onConfirm={() => void doDelete(dialog.version)}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
};
